import { Sparkles } from "lucide-react";
import { type RefObject, useMemo, useState } from "react";

const canHover =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover)").matches;

const shortcutLabel = canHover
  ? typeof navigator !== "undefined" &&
    /Macintosh|Mac OS X/i.test(navigator.userAgent)
    ? "⌘I"
    : "Ctrl+I"
  : null;

interface IAssistantLauncherProps {
  triggerRef: RefObject<HTMLDivElement | null>;
  /** Left/bottom anchor in viewport px — pins the launcher to the content area's bottom-left. */
  anchorLeftPx: number;
  anchorBottomPx: number;
  autoFocus?: boolean;
  onOpen: () => void;
}

export const AssistantLauncher = ({
  triggerRef,
  anchorLeftPx,
  anchorBottomPx,
  autoFocus = false,
  onOpen,
}: IAssistantLauncherProps) => {
  const [hovered, setHovered] = useState(false);

  const wrapStyle = useMemo(
    () => ({
      gridTemplateColumns: hovered ? "1fr" : "0fr",
      marginLeft: hovered ? "10px" : "0",
      transition:
        "grid-template-columns 220ms cubic-bezier(0.16,1,0.3,1), margin-left 220ms cubic-bezier(0.16,1,0.3,1)",
    }),
    [hovered],
  );

  return (
    <div
      ref={triggerRef}
      className="fixed z-[400002] flex flex-col items-start"
      style={{ left: anchorLeftPx, bottom: anchorBottomPx }}
    >
      <button
        type="button"
        // biome-ignore lint/a11y/noAutofocus: focus returns to the launcher when the panel closes
        autoFocus={autoFocus}
        aria-label="Ask about this case"
        className="flex h-[52px] animate-in cursor-pointer items-center rounded-full bg-primary pl-[18px] pr-5 text-base-strong text-white shadow-[0_8px_24px_-6px_rgba(57,0,140,0.55),0_2px_6px_rgba(0,0,0,0.12)] fade-in-[0.4] zoom-in-[0.92] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] transition-transform hover:-translate-y-0.5 motion-reduce:animate-none"
        onClick={onOpen}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Sparkles className="size-5 shrink-0" />

        {canHover && (
          <span className="grid min-w-0 overflow-hidden" style={wrapStyle}>
            <span className="min-w-0 overflow-hidden whitespace-nowrap">
              Ask about this case
            </span>
          </span>
        )}

        {shortcutLabel && (
          <span className="ml-2.5 shrink-0 text-xs font-normal text-white/70">
            {shortcutLabel}
          </span>
        )}
      </button>
    </div>
  );
};
